import { useState, useEffect } from "react";
import Select from "@/components/base/Select";
import Badge from "@/components/base/Badge";
import { useProvider } from "@/hooks/useProvider";
import { supabase } from "@/lib/supabase";
import { fetchTeamUsers } from "@/lib/data";
import { roles, teamUsers as mockTeamUsers } from "@/mocks/settings";
import type { TeamUser } from "@/mocks/settings";

const roleOptions = roles.map((r) => ({ value: r.name, label: r.name }));

const roleIcons: Record<string, string> = {
  مدير: "ri-shield-star-line",
  "مشرف طلبات": "ri-clipboard-line",
  محاسب: "ri-calculator-line",
  عارض: "ri-eye-line",
};

const avatarTones = [
  "from-[#00AAB7] to-[#0D6E6E]",
  "from-[#33D1DA] to-[#00AAB7]",
  "from-[#1A9E96] to-[#0D6E6E]",
  "from-[#5CC9D0] to-[#1A9E96]",
];

const initialsOf = (name: string) =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("");

export default function ProviderUsers() {
  const { provider, loading: providerLoading } = useProvider();

  const [users, setUsers] = useState<TeamUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [userAdded, setUserAdded] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", role: "مشرف طلبات" });

  useEffect(() => {
    setLoading(true);
    if (!provider) {
      // وضع المعاينة: عرض بيانات تجريبية حتى لا تعلق الصفحة على التحميل
      setUsers(mockTeamUsers);
      setLoading(false);
      return;
    }
    fetchTeamUsers(provider.id)
      .then(setUsers)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [provider]);

  const handleAddUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim()) return;

    if (!provider) {
      setUsers((prev) => [
        ...prev,
        {
          id: Date.now(),
          name: newUser.name.trim(),
          email: newUser.email.trim(),
          role: newUser.role,
          status: "نشط",
          isMe: false,
        },
      ]);
      setNewUser({ name: "", email: "", role: "مشرف طلبات" });
      setAddUserOpen(false);
      setUserAdded(true);
      setTimeout(() => setUserAdded(false), 4000);
      return;
    }

    const { data, error } = await supabase
      .from("team_users")
      .insert({
        provider_id: provider.id,
        name: newUser.name.trim(),
        email: newUser.email.trim(),
        role: newUser.role,
        status: "نشط",
        is_me: false,
      })
      .select()
      .single();

    if (!error && data) {
      setUsers((prev) => [
        ...prev,
        { id: data.id, name: data.name, email: data.email, role: data.role, status: data.status, isMe: false },
      ]);
    }
    setNewUser({ name: "", email: "", role: "مشرف طلبات" });
    setAddUserOpen(false);
    setUserAdded(true);
    setTimeout(() => setUserAdded(false), 4000);
  };

  const toggleUserStatus = (id: number) => {
    const target = users.find((u) => u.id === id);
    if (!target) return;
    const nextStatus = target.status === "نشط" ? "موقوف" : "نشط";
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, status: nextStatus } : u)));
    supabase.from("team_users").update({ status: nextStatus }).eq("id", id);
  };

  const inputClass =
    "h-12 w-full rounded-[14px] border border-[#E4F5F5] bg-white px-4 text-sm text-ghasla-text-heading outline-none transition-colors focus:border-[#00AAB7]";
  const labelClass = "mb-2 block text-sm font-medium text-ghasla-text-primary";

  if (providerLoading || loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#E4F5F5] border-t-[#0D6E6E]" />
      </div>
    );
  }

  const activeCount = users.filter((u) => u.status === "نشط").length;
  const stoppedCount = users.filter((u) => u.status === "موقوف").length;

  const stats = [
    { label: "إجمالي المستخدمين", value: users.length, icon: "ri-group-line", tone: "#00AAB7", bg: "#E8F7F8" },
    { label: "نشط حالياً", value: activeCount, icon: "ri-checkbox-circle-line", tone: "#27AE60", bg: "#E8F9F0" },
    { label: "موقوف", value: stoppedCount, icon: "ri-forbid-line", tone: "#E74C3C", bg: "#FFF5F5" },
    { label: "الأدوار المتاحة", value: roles.length, icon: "ri-shield-user-line", tone: "#D97706", bg: "#FFF5E6" },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-[28px] font-bold text-ghasla-text-heading">المستخدمون والصلاحيات</h1>
          <p className="mt-1 text-sm text-ghasla-text-secondary">
            أضف أعضاء فريقك وحدد صلاحيات كل واحد فيهم
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAddUserOpen((v) => !v)}
          className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          <i className="ri-user-add-line text-lg" />
          إضافة مستخدم
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-[20px] bg-white p-5 shadow-card">
            <div className="flex items-center justify-between">
              <span
                className="flex h-11 w-11 items-center justify-center rounded-full"
                style={{ backgroundColor: s.bg, color: s.tone }}
              >
                <i className={`${s.icon} text-xl`} />
              </span>
              <span className="text-3xl font-bold text-ghasla-text-heading">{s.value}</span>
            </div>
            <p className="mt-3 text-sm text-ghasla-text-secondary">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Add user form */}
      {addUserOpen && (
        <section className="rounded-[20px] border border-[#D9F0F1] bg-white p-5 shadow-card">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2 text-sm font-semibold text-ghasla-text-heading">
              <i className="ri-user-add-line text-lg text-ghasla-text-link" />
              إضافة مستخدم جديد
            </p>
            <button
              type="button"
              onClick={() => setAddUserOpen(false)}
              className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-ghasla-text-secondary hover:bg-[#F0FDFD]"
              aria-label="إغلاق"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className={labelClass}>الاسم الكامل</label>
              <input
                className={inputClass}
                placeholder="مثال: عبدالله السالم"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              />
            </div>
            <div>
              <label className={labelClass}>الإيميل</label>
              <input
                className={inputClass}
                type="email"
                dir="ltr"
                placeholder="name@company.sa"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
            </div>
            <div>
              <label className={labelClass}>الدور</label>
              <Select
                value={newUser.role}
                onChange={(v) => setNewUser({ ...newUser, role: v })}
                options={roleOptions}
              />
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={handleAddUser}
              className="cursor-pointer whitespace-nowrap rounded-xl bg-gradient-to-l from-[#0D6E6E] to-[#1A9E96] px-6 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              إضافة المستخدم
            </button>
            <button
              type="button"
              onClick={() => setAddUserOpen(false)}
              className="cursor-pointer whitespace-nowrap rounded-xl border border-[#E4EFEF] bg-white px-5 py-2.5 text-sm font-medium text-ghasla-text-primary transition-colors hover:bg-[#F0FDFD]"
            >
              إلغاء
            </button>
          </div>
        </section>
      )}

      {/* Users table */}
      <section className="rounded-[20px] bg-white p-6 shadow-card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-ghasla-text-heading">أعضاء الفريق</h2>
          <span className="rounded-full bg-[#F0FDFD] px-3 py-1 text-xs font-medium text-ghasla-text-link">
            {users.length} مستخدم
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] text-sm">
            <thead>
              <tr className="text-xs font-semibold text-ghasla-text-secondary">
                <th className="h-11 px-3 text-right">المستخدم</th>
                <th className="h-11 px-3 text-right">الدور</th>
                <th className="h-11 px-3 text-right">الحالة</th>
                <th className="h-11 px-3 text-left">إجراء</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} className="border-t border-[#F4F8F8] transition-colors hover:bg-[#FAFEFE]">
                  <td className="h-16 px-3">
                    <div className="flex items-center gap-3">
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-xs font-semibold text-white ${avatarTones[i % avatarTones.length]}`}
                      >
                        {initialsOf(u.name)}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-ghasla-text-heading">
                          {u.name}
                          {u.isMe && (
                            <span className="mr-2 rounded-full bg-[#E8F7F8] px-2 py-0.5 text-[10px] font-medium text-ghasla-text-link">
                              أنت
                            </span>
                          )}
                        </p>
                        <p className="truncate text-xs text-ghasla-text-secondary" dir="ltr">
                          {u.email}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="h-16 px-3">
                    <Badge tone="teal">
                      <i className={`${roleIcons[u.role] ?? "ri-user-line"}`} />
                      {u.role}
                    </Badge>
                  </td>
                  <td className="h-16 px-3">
                    <Badge tone={u.status === "موقوف" ? "gray" : "green"}>
                      <i className="ri-circle-fill text-[7px]" />
                      {u.status}
                    </Badge>
                  </td>
                  <td className="h-16 px-3">
                    {u.isMe ? (
                      <span className="text-xs text-ghasla-text-secondary">—</span>
                    ) : (
                      <div className="flex items-center justify-start gap-2">
                        <button
                          type="button"
                          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-[#E4EFEF] text-ghasla-text-primary transition-colors hover:bg-[#F0FDFD]"
                          aria-label="تعديل"
                          title="تعديل"
                        >
                          <i className="ri-edit-line text-base" />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleUserStatus(u.id)}
                          className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border transition-colors ${
                            u.status === "موقوف"
                              ? "border-[#C8EFD9] text-[#27AE60] hover:bg-[#E8F9F0]"
                              : "border-[#F5D4D0] text-[#E74C3C] hover:bg-[#FFF5F5]"
                          }`}
                          aria-label={u.status === "موقوف" ? "تفعيل" : "إيقاف"}
                          title={u.status === "موقوف" ? "تفعيل" : "إيقاف"}
                        >
                          <i className={`${u.status === "موقوف" ? "ri-play-line" : "ri-pause-line"} text-base`} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={4} className="h-16 text-center text-sm text-ghasla-text-secondary">
                    لا يوجد مستخدمون بعد
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Roles */}
      <section>
        <h2 className="mb-3 text-base font-semibold text-ghasla-text-heading">الأدوار والصلاحيات</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {roles.map((r) => (
            <div
              key={r.name}
              className="rounded-[18px] border border-[#E4F5F5] bg-white p-4 transition-colors hover:border-[#BEE7E9]"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E8F7F8] text-ghasla-text-link">
                <i className={`${roleIcons[r.name] ?? "ri-user-line"} text-lg`} />
              </span>
              <p className="mt-3 text-sm font-semibold text-ghasla-text-heading">{r.name}</p>
              <p className="mt-1 text-xs leading-5 text-ghasla-text-secondary">{r.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* User added toast */}
      {userAdded && (
        <div className="fixed bottom-8 left-8 z-50 flex items-center gap-2 rounded-2xl bg-[#E8F9F0] px-5 py-4 shadow-card">
          <i className="ri-checkbox-circle-fill text-xl text-[#27AE60]" />
          <span className="text-sm font-medium text-ghasla-text-heading">
            تمت إضافة المستخدم ✓ — وصلته بيانات الدخول عبر الإيميل/الرسائل
          </span>
        </div>
      )}
    </div>
  );
}